import { getGroupDisplayName, getItemDisplayName, getTaskBucket, TASK_BUCKET } from './helpers';
import { formatTemplateType } from './models';

export type AssetRowType = 'template' | 'group' | 'item';

export type AssetStatus = 'Operational' | 'Maintenance' | 'Offline';

export interface AssetRow {
  key: string;
  id: number;
  type: AssetRowType;
  raw: any;
  name: string;
  description: string;
  area: string;
  enabled: boolean;
  /** Live per-site status; null for a template the site has not deployed. */
  status: AssetStatus | null;
  imageUrl: string | null;
  workOrders: number;
  inventory: number;
  prevMaint: number;
  dailyTasks: number;
}

export const UNASSIGNED_AREA = '—';

/**
 * The area a row sits in is the AssetTemplate `type` -- "Outdoor", "Facility
 * Infrastructure" and so on. A template states its own; groups and items
 * inherit it from the templates they are linked to.
 */
export const getAreaLabel = (entity: any, type: AssetRowType): string => {
  if (type === 'template') {
    return formatTemplateType(entity?.type) || UNASSIGNED_AREA;
  }
  const types = Array.from(
    new Set(
      (entity?.assetTemplates || [])
        .map((template: any) => formatTemplateType(template?.type))
        .filter((value: any) => !!value)
    )
  );
  return types.length ? types.join(', ') : UNASSIGNED_AREA;
};

/** Distinct template types, for the Area filter's options. */
export const getAreaOptions = (templates: any[]): string[] =>
  Array.from(new Set((templates || []).map((t: any) => t?.type).filter(Boolean))).sort();

/** Template types a row can be matched against by the Area filter. */
export const getAreaValues = (entity: any, type: AssetRowType): string[] => {
  if (type === 'template') return entity?.type ? [entity.type] : [];
  return (entity?.assetTemplates || []).map((t: any) => t?.type).filter(Boolean);
};

const isOpen = (occurrence: any): boolean => !occurrence?.archivedAt && !occurrence?.completedAt;

/**
 * Gathers a relation across a whole subtree, deduped by row id. A task or part
 * can be reachable both from the template and from the sub-asset it sits on,
 * and it should be counted once.
 */
const collectRelation = (entities: any[], relation: string): any[] => {
  const byId = new Map<number, any>();
  const unkeyed: any[] = [];
  entities.forEach((entity: any) => {
    (entity?.[relation] || []).forEach((row: any) => {
      const id = Number(row?.id);
      if (Number.isFinite(id)) byId.set(id, row);
      else unkeyed.push(row);
    });
  });
  return [...Array.from(byId.values()), ...unkeyed];
};

const countOpenInBucket = (occurrences: any[], bucket: string): number =>
  occurrences.filter(
    (occurrence: any) =>
      isOpen(occurrence) && getTaskBucket(occurrence?.task?.type ?? occurrence?.type) === bucket
  ).length;

const countLiveInventory = (inventories: any[]): number =>
  inventories.filter((inventory: any) => !inventory?.archivedAt).length;

export const getOpenWorkOrderCount = (entity: any): number =>
  countOpenInBucket(entity?.taskOccurrences || [], TASK_BUCKET.Project);

export const getPrevMaintCount = (entity: any): number =>
  countOpenInBucket(entity?.taskOccurrences || [], TASK_BUCKET.PrevMaint);

export const getDailyTaskCount = (entity: any): number =>
  countOpenInBucket(entity?.taskOccurrences || [], TASK_BUCKET.DailyTask);

export const getInventoryCount = (entity: any): number =>
  countLiveInventory(entity?.inventories || []);

/** Every entity a rollup can draw counts from, regardless of what is filtered. */
export interface AssetRowScope {
  groups?: any[];
  items?: any[];
  /** Operational status per template id, read off the site's occurrences. */
  statusByTemplateId?: Map<number, AssetStatus>;
  /**
   * Open work orders per template id, counted from this site's occurrence.
   * Falls back to the blueprint rollup for templates the site has not
   * deployed, where there is no occurrence to count.
   */
  workOrdersByTemplateId?: Map<number, number>;
}

const linksTo = (entity: any, relation: string, id: number): boolean =>
  (entity?.[relation] || []).some((linked: any) => Number(linked?.id) === id);

/**
 * One flat row set covering templates, groups and sub-assets.
 *
 * Templates and groups report their whole subtree: a group adds the sub-assets
 * filed under it, and a template adds its groups plus everything in them. The
 * rollup reads from `scope`, not the filtered rows, so narrowing the table by
 * type or area never changes a count.
 */
export const buildAssetRows = (
  templates: any[],
  groups: any[],
  items: any[],
  scope: AssetRowScope = {}
): AssetRow[] => {
  const allGroups = scope.groups ?? groups ?? [];
  const allItems = scope.items ?? items ?? [];
  const statusByTemplateId = scope.statusByTemplateId ?? new Map<number, AssetStatus>();
  const workOrdersByTemplateId = scope.workOrdersByTemplateId ?? new Map<number, number>();

  const itemsInGroup = (groupId: number): any[] =>
    allItems.filter(
      (item: any) => Number(item?.assetGroupId) === groupId || linksTo(item, 'assetGroups', groupId)
    );

  const subtreeOf = (raw: any, type: AssetRowType): any[] => {
    const id = Number(raw?.id);
    if (type === 'item') return [raw];
    if (type === 'group') return [raw, ...itemsInGroup(id)];

    const templateGroups = allGroups.filter((group: any) => linksTo(group, 'assetTemplates', id));
    const directItems = allItems.filter((item: any) => linksTo(item, 'assetTemplates', id));
    const groupItems = templateGroups.flatMap((group: any) => itemsInGroup(Number(group.id)));
    const byId = new Map<number, any>();
    [...directItems, ...groupItems].forEach((item: any) => byId.set(Number(item.id), item));
    return [raw, ...templateGroups, ...Array.from(byId.values())];
  };

  const toRow = (raw: any, type: AssetRowType, name: string, area: string): AssetRow => {
    const subtree = subtreeOf(raw, type);
    // Everything the row owns: its own tasks and parts, plus those of every
    // group and sub-asset beneath it.
    const occurrences = collectRelation(subtree, 'taskOccurrences');
    const inventories = collectRelation(subtree, 'inventories');
    return {
      key: `${type}-${raw.id}`,
      id: Number(raw.id),
      type,
      raw,
      name,
      description: raw.notes || '',
      area,
      enabled: !raw?.archivedAt,
      status: type === 'template' ? statusByTemplateId.get(Number(raw.id)) ?? null : null,
      imageUrl: raw?.image?.downloadUrl ?? raw?.assetTemplate?.image?.downloadUrl ?? null,
      workOrders:
        type === 'template' && workOrdersByTemplateId.has(Number(raw.id))
          ? (workOrdersByTemplateId.get(Number(raw.id)) as number)
          : countOpenInBucket(occurrences, TASK_BUCKET.Project),
      inventory: countLiveInventory(inventories),
      prevMaint: countOpenInBucket(occurrences, TASK_BUCKET.PrevMaint),
      dailyTasks: countOpenInBucket(occurrences, TASK_BUCKET.DailyTask),
    };
  };

  return [
    ...(templates || []).map((template: any) =>
      toRow(template, 'template', template.name || '', getAreaLabel(template, 'template'))
    ),
    ...(groups || []).map((group: any) =>
      toRow(group, 'group', getGroupDisplayName(group), getAreaLabel(group, 'group'))
    ),
    ...(items || []).map((item: any) =>
      toRow(item, 'item', getItemDisplayName(item), getAreaLabel(item, 'item'))
    ),
  ];
};
